import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import i18next from "../../i18n/i18n";
import { getUserContentFilePath } from "../../api";
import { Attachment, getFileProperties, ServerSideFile } from "./FileAttachments";
import { FilePreviewDialog } from "./FilePreviewDialog";
import { IMAGE_TYPES } from "./fileUtils";

function fileIcon(mime: string) {
    if (!mime) return <InsertDriveFileIcon color="primary" />;
    if (mime.startsWith("image/")) return <ImageIcon color="primary" />;
    if (mime === "application/pdf") return <DescriptionIcon color="error" />;
    return <InsertDriveFileIcon color="primary" />;
}

function getReadableSize(size: number) {
    if (size < 1024) return size + " B";
    if (size < 1024 * 1024) return (size / 1024).toFixed(1) + " KB";
    return (size / (1024 * 1024)).toFixed(2) + " MB";
}

export function ChatAreaFileRender({
    files,
    bubbleStyle = {},
    openRequest
}: {
    files: Array<Attachment>;
    bubbleStyle?: React.CSSProperties;
    openRequest?: { index: number; nonce: number; page?: number };
}) {
    const [expanded, setExpanded] = useState(true);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [currentPageToView, setCurrentPageToView] = useState(0);
    const [currentTargetIndex, setCurrentTargetIndex] = useState(0);
    const [imageSources, setImageSources] = useState<Map<string, string>>(new Map());

    useEffect(() => {
        const sources = new Map<string, string>();
        const createdObjectUrls: string[] = [];

        files.forEach(file => {
            const { type: fileType } = getFileProperties(file);
            if (!IMAGE_TYPES.includes(fileType)) {
                return;
            }

            if (file.preview) {
                sources.set(file.id, file.preview);
                return;
            }

            if (file.file instanceof File) {
                const objectUrl = URL.createObjectURL(file.file);
                sources.set(file.id, objectUrl);
                createdObjectUrls.push(objectUrl);
                return;
            }

            const serverFile = file.file as ServerSideFile;
            if (serverFile.content_path) {
                sources.set(file.id, getUserContentFilePath(serverFile.content_path));
            } else if (typeof serverFile.content === "string" && serverFile.content.length > 0) {
                sources.set(file.id, `data:${fileType};base64,${serverFile.content}`);
            }
        });

        setImageSources(sources);

        return () => {
            createdObjectUrls.forEach(url => URL.revokeObjectURL(url));
        };
    }, [files]);

    useEffect(() => {
        const openIndex = openRequest?.index;
        if (typeof openIndex === "number" && openIndex >= 0 && openIndex < files.length) {
            setCurrentTargetIndex(openIndex);
            setCurrentPageToView(openRequest?.page ?? 0);
            setPreviewDialogOpen(true);
        }
    }, [openRequest?.nonce, openRequest?.index, openRequest?.page, files.length]);

    return (
        <Box display="flex" flexDirection={"row"} alignItems="flex-end" sx={{ my: 1 }}>
            <Paper
                elevation={1}
                sx={{
                    background: "#e8ebfa",
                    borderRadius: 2,
                    px: 0,
                    py: 0,
                    minWidth: 220,
                    maxWidth: 380,
                    position: "relative",
                    ...bubbleStyle
                }}
            >
                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color="#5A5A5A!important"
                    fontFamily="inherit"
                    fontStyle="inherit"
                    noWrap
                    title={expanded ? i18next.t("chat.collapseAttachments") : i18next.t("chat.expandAttachments")}
                    sx={{
                        margin: "16px 32px 0px 16px"
                    }}
                >
                    {i18next.t("chat.uploadedFiles")}
                    <ChevronRightIcon
                        fontSize="small"
                        onClick={() => setExpanded(e => !e)}
                        sx={{
                            transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                            transition: "transform 0.2s",
                            color: "#7878b1",
                            position: "absolute",
                            right: 4,
                            top: 8,
                            bgcolor: "#fff",
                            borderRadius: "50%",
                            boxShadow: 1,
                            zIndex: 2
                        }}
                    />
                </Typography>

                <Box display={expanded ? "flex" : "none"} flexDirection="column" gap={0.25} pt={3} pb={1.5} px={2} minWidth={180} maxWidth={340}>
                    {files.map((f, idx) => {
                        const { name: fileName, type: fileType, size: fileSize } = getFileProperties(f);
                        const isImage = IMAGE_TYPES.includes(fileType);
                        const imageSrc = imageSources.get(f.id);

                        return (
                            <Paper
                                key={f.id}
                                elevation={0}
                                onClick={() => {
                                    setPreviewDialogOpen(true);
                                    setCurrentTargetIndex(idx);
                                }}
                                sx={{
                                    background: "#fff",
                                    borderRadius: 2,
                                    boxShadow: "0 1px 4px #0001",
                                    py: 1.2,
                                    px: 1.4,
                                    mb: 0.3,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.2,
                                    minWidth: 0,
                                    overflow: "hidden",
                                    cursor: "pointer"
                                }}
                            >
                                {isImage && imageSrc ? (
                                    <Box
                                        component="img"
                                        src={imageSrc}
                                        alt={fileName}
                                        sx={{
                                            width: 46,
                                            height: 46,
                                            borderRadius: 1.3,
                                            objectFit: "cover",
                                            bgcolor: "#f5f6fa",
                                            mr: 1
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 38,
                                            height: 38,
                                            borderRadius: 1.1,
                                            bgcolor: "#e8ebfa",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mr: 1
                                        }}
                                    >
                                        {fileIcon(fileType)}
                                    </Box>
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        color="#5A5A5A!important"
                                        noWrap
                                        title={fileName}
                                        sx={{
                                            maxWidth: 140
                                        }}
                                    >
                                        {fileName}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={0.8} mt={0.3} sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" color="#999">
                                            {getReadableSize(fileSize)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>
                {previewDialogOpen && (
                    <FilePreviewDialog
                        files={files}
                        initialIndex={currentTargetIndex}
                        open={previewDialogOpen}
                        currentPageToView={currentPageToView}
                        onClose={() => {
                            setPreviewDialogOpen(false);
                            setCurrentTargetIndex(0);
                            setCurrentPageToView(0);
                        }}
                    />
                )}
            </Paper>
        </Box>
    );
}
